/**
 * Composio Client Service
 * -----------------------
 * Client service for integrating with Composio platform via the MyAMP API layer.
 * Enables insurance agents to connect personal apps for AI-powered automation.
 */

if (!process.env.REACT_APP_MyAMP_Url) {
  throw new Error(
    "REACT_APP_MyAMP_Url is not set in .env — cannot initialise Composio client",
  );
}
const COMPOSIO_API_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/v1/composio`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SessionInfo {
  user_id: string;
  entity_id: string;
  toolkits: string[];
  connected_apps: string[];
  setup_required: boolean;
}

export interface ConnectedApp {
  app: string;
  app_display_name: string;
  icon: string;
  status:
    | "pending"
    | "active"
    | "failed"
    | "disconnected"
    | "expired"
    | "initiated"
    | "revoked";
  connection_id: string | null;
  connected_at: string | null;
  last_used_at: string | null;
  /**
   * Set on tiles that use another app's connection (Outlook Calendar ->
   * "outlook"): the status mirrors that app and there is nothing of its own
   * to disconnect.
   */
  linked_app?: string;
}

export interface AvailableApp {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: string;
  composio_app: string;
  coming_soon?: boolean;
  custom_setup?: boolean;
  /**
   * Id of the app whose Composio connection this tile uses (Outlook Calendar
   * -> "outlook"). Connecting the tile runs that app's OAuth flow, its status
   * mirrors that app, and disconnecting that app removes it.
   */
  linked_app?: string;
}

export interface ConnectAppResult {
  auth_url: string;
  connection_id: string | null;
  app_name: string;
  status: string;
}

export interface ExecuteToolResult {
  success: boolean;
  result: Record<string, unknown>;
  tool_name: string;
  execution_id: string;
}

export interface ComposioStatus {
  enabled: boolean;
  setup_required?: boolean;
  has_key?: boolean;
  connected_apps_count: number;
  connected_apps: string[];
  has_any_connection: boolean;
  message?: string;
  error?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code: string;
    details?: Record<string, unknown>;
  };
}

// ── Available Apps Configuration ──────────────────────────────────────────────

/**
 * App ids that stay configured (OAuth setup guide, toolkit mapping) but are
 * not shown on the Connected Apps page. The API can return them too, so the
 * list is filtered by this set rather than by trimming AVAILABLE_APPS. Remove
 * an id here to show its tile again.
 */
export const HIDDEN_APP_IDS: ReadonlySet<string> = new Set(["microsoft_teams"]);

export function isAppVisible(app: Pick<AvailableApp, "id">): boolean {
  return !HIDDEN_APP_IDS.has(app.id.toLowerCase());
}

export const AVAILABLE_APPS: AvailableApp[] = [
  {
    id: "gmail",
    name: "Gmail",
    icon: "📧",
    description: "Send and manage emails for client communications",
    category: "Communication",
    composio_app: "GMAIL",
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    icon: "📅",
    description: "Schedule meetings and appointments with clients",
    category: "Productivity",
    composio_app: "GOOGLECALENDAR",
  },
  {
    id: "google_drive",
    name: "Google Drive",
    icon: "📁",
    description: "Store and share insurance documents",
    category: "Storage",
    composio_app: "GOOGLEDRIVE",
  },
  {
    id: "google_sheets",
    name: "Google Sheets",
    icon: "📊",
    description: "Read and update spreadsheets for reports and client data",
    category: "Productivity",
    composio_app: "GOOGLESHEETS",
  },
  {
    id: "google_chat",
    name: "Google Chat",
    icon: "💬",
    description:
      "Post messages to Google Chat spaces, group chats and direct messages",
    category: "Communication",
    composio_app: "GOOGLE_CHAT",
  },
  {
    id: "jotform",
    name: "JotForms",
    icon: "📋",
    description: "Form submissions and lead capture for your agency",
    category: "Forms",
    composio_app: "JOTFORM",
  },
  {
    id: "outlook",
    name: "Outlook 365",
    icon: "📨",
    description: "Send and manage emails via Microsoft Outlook",
    category: "Communication",
    composio_app: "OUTLOOK",
  },
  {
    // Rides on the Outlook 365 connection: Composio keeps the Microsoft 365
    // calendar actions inside the OUTLOOK toolkit.
    id: "outlook_calendar",
    name: "Outlook Calendar",
    icon: "📅",
    description:
      "Schedule meetings, find free slots and manage events on your Outlook 365 calendar",
    category: "Productivity",
    composio_app: "OUTLOOK",
    linked_app: "outlook",
  },
  {
    id: "microsoft_teams",
    name: "Microsoft Teams",
    icon: "👥",
    description: "Post to Teams channels and chats and schedule Teams meetings",
    category: "Communication",
    composio_app: "MICROSOFT_TEAMS",
  },
  {
    id: "slack",
    name: "Slack",
    icon: "💬",
    description: "Team communication and notifications",
    category: "Communication",
    composio_app: "SLACK",
    coming_soon: true,
  },
  {
    id: "hubspot",
    name: "HubSpot",
    icon: "🎯",
    description: "CRM and client management",
    category: "CRM",
    composio_app: "HUBSPOT",
    coming_soon: true,
  },
  {
    id: "docusign",
    name: "DocuSign",
    icon: "✍️",
    description: "Send and track insurance documents for e-signature",
    category: "Documents",
    composio_app: "DOCUSIGN",
    coming_soon: true,
  },
  {
    id: "lightspeed_voice",
    name: "Lightspeed Voice",
    icon: "📞",
    description:
      "Receive call recordings, auto-transcribe with AI, and analyse every client call in your agency.",
    category: "Voice",
    composio_app: "",
    custom_setup: true,
  },
  {
    id: "momentum_voice",
    name: "Momentum Voice",
    icon: "🎙️",
    description:
      "Turn every Momentum Voice call summary into a Kanban task automatically.",
    category: "Voice",
    composio_app: "",
  },
  {
    id: "momentum_sms",
    name: "Momentum SMS",
    icon: "💬",
    description:
      "Turn the AMS text messages you are notified about into Kanban tasks automatically.",
    category: "SMS",
    composio_app: "",
  },
];

// Toolkits whose tile shows "Set Up" instead of "Connect" until the current
// user has saved their own OAuth client via the IntegrationsPage modal. The
// client is per user (the API lists only the caller's rows), exactly like
// every user connecting their own Gmail.
// Google Chat is deliberately NOT listed: it shows a plain Connect button and
// the same Composio hosted dialog as Gmail. Composio has no managed Google
// OAuth app for Chat, so if the user has no client yet the API answers
// OAUTH_APP_SETUP_REQUIRED and the page opens the setup dialog from there
// (see OAuthSetupRequiredError / `_CUSTOM_OAUTH_ONLY_TOOLKITS` in the API).
export const TOOLKITS_REQUIRING_OAUTH_SETUP: string[] = ["docusign"];

/**
 * Redirect URIs that must be registered on the customer's OAuth client for the
 * toolkits above. Composio completes the provider handshake there and then
 * redirects back to our /settings/integrations/callback page.
 * Observed on stage 2026-10-06: Google's redirect_uri_mismatch details showed
 * Composio using the v1 path for an auth config created via the v3 API, while
 * Composio's v3 docs name the v3 path. Register both; the first is the one
 * Google actually received.
 */
export const COMPOSIO_OAUTH_CALLBACK_URLS: readonly string[] = [
  "https://backend.composio.dev/api/v1/auth-apps/add",
  "https://backend.composio.dev/api/v3/toolkits/auth/callback",
];
export const COMPOSIO_OAUTH_CALLBACK_URL = COMPOSIO_OAUTH_CALLBACK_URLS[0];

/** Per-toolkit wording for the one-time OAuth "Set up" modal. */
export interface OAuthSetupGuide {
  displayName: string;
  clientIdLabel: string;
  clientSecretLabel: string;
  clientIdPlaceholder: string;
  clientSecretPlaceholder: string;
  /** Where the admin finds the credentials. */
  consoleHint: string;
  /** Ordered how-to for obtaining the client ID and secret, shown in the dialog. */
  steps: string[];
}

/** Delegated Microsoft Graph scopes the API requests for each Microsoft 365 toolkit. */
const MICROSOFT_OUTLOOK_SCOPES = [
  "Mail.Read",
  "Mail.Send",
  "Mail.ReadWrite",
  "Calendars.ReadWrite",
  "User.Read",
  "offline_access",
];
const MICROSOFT_TEAMS_SCOPES = [
  "Team.ReadBasic.All",
  "Channel.ReadBasic.All",
  "ChannelMessage.Send",
  "Chat.ReadWrite",
  "ChatMessage.Send",
  "OnlineMeetings.ReadWrite",
  "User.Read",
  "User.ReadBasic.All",
  "offline_access",
];

/**
 * Microsoft Entra app-registration steps shared by the Microsoft 365 toolkits.
 * Composio hosts a managed OAuth app for Outlook and Teams, so this guide only
 * appears when a user chooses to bring their own client.
 */
function microsoftEntraSteps(product: string, scopes: string[]): string[] {
  return [
    "Sign in to the Microsoft Entra admin center (entra.microsoft.com) with an account allowed to register applications, open Identity > Applications > App registrations and click New registration.",
    `Name the app (e.g. Momentum AMP ${product}). Under Supported account types choose "Accounts in any organizational directory" (or your organization only when everyone connecting is in your tenant). Under Redirect URI pick Web and add the first URI listed below; after the app is created, open Authentication and add the second URI as well.`,
    "On the app's Overview page copy the Application (client) ID. That is the Client ID.",
    "Open Certificates & secrets > Client secrets > New client secret, give it a description and expiry, and copy the secret's Value right away (Microsoft shows it only once). Paste the Value below, not the Secret ID.",
    `Open API permissions > Add a permission > Microsoft Graph > Delegated permissions and add: ${scopes.join(", ")}. Click Grant admin consent if your tenant requires it.`,
    "Paste the Client ID and the secret Value into the fields below, save, then click Connect on the tile.",
  ];
}

const OAUTH_SETUP_GUIDES: Record<string, OAuthSetupGuide> = {
  outlook: {
    displayName: "Outlook 365",
    clientIdLabel: "Application (client) ID",
    clientSecretLabel: "Client secret value",
    clientIdPlaceholder: "e.g. 11111111-2222-3333-4444-555555555555",
    clientSecretPlaceholder: "Secret Value from Certificates & secrets",
    consoleHint: "Microsoft Entra admin center (App registrations)",
    steps: microsoftEntraSteps("Outlook", MICROSOFT_OUTLOOK_SCOPES),
  },
  microsoft_teams: {
    displayName: "Microsoft Teams",
    clientIdLabel: "Application (client) ID",
    clientSecretLabel: "Client secret value",
    clientIdPlaceholder: "e.g. 11111111-2222-3333-4444-555555555555",
    clientSecretPlaceholder: "Secret Value from Certificates & secrets",
    consoleHint: "Microsoft Entra admin center (App registrations)",
    steps: microsoftEntraSteps("Teams", MICROSOFT_TEAMS_SCOPES),
  },
  docusign: {
    displayName: "DocuSign",
    clientIdLabel: "Integration Key (Client ID)",
    clientSecretLabel: "Secret Key",
    clientIdPlaceholder: "e.g. 00000000-0000-0000-0000-000000000000",
    clientSecretPlaceholder: "Secret Key from the developer console",
    consoleHint: "DocuSign developer console (Settings → Apps and Keys)",
    steps: [
      "Sign in to DocuSign Admin (the developer sandbox or your production account) and open Settings → Apps and Keys.",
      "Click Add App and Integration Key, name the app, and copy the Integration Key. That is the Client ID.",
      "Under Authentication choose Authorization Code Grant and click Add Secret Key. Copy the Secret Key immediately; DocuSign shows it only once.",
      "Under Redirect URIs add both URIs listed below, exactly as shown, then Save.",
      "Paste the Integration Key and Secret Key into the fields below.",
    ],
  },
  google_chat: {
    displayName: "Google Chat",
    clientIdLabel: "OAuth Client ID",
    clientSecretLabel: "OAuth Client Secret",
    clientIdPlaceholder: "e.g. 123456789012-abc123.apps.googleusercontent.com",
    clientSecretPlaceholder: "Client secret from Google Cloud Console",
    consoleHint:
      "Google Cloud Console (APIs & Services → Credentials → OAuth 2.0 Client ID, type Web application)",
    steps: [
      "Open Google Cloud Console (console.cloud.google.com) and select the project that will own the integration, or create one.",
      "APIs & Services → Library: search for Google Chat API and click Enable. Then open the API's Configuration tab and save it with an App name (up to 25 alphanumeric characters, e.g. Momentum AMP), an Avatar URL (https link to a square PNG or JPEG) and a Description (up to 40 alphanumeric characters); leave Interactive features off. Google rejects message sends with \"Chat app not found\" until this is saved in the same project as the OAuth client.",
      "APIs & Services → OAuth consent screen (also called Google Auth Platform): choose External, set the app name and support email, add the scopes https://www.googleapis.com/auth/chat.messages.create and https://www.googleapis.com/auth/chat.spaces.readonly, and add your own Google account as a test user. Only you connect through this client, so the app can stay in Testing; publish it only if you want to skip the test-user list.",
      "APIs & Services → Credentials → Create credentials → OAuth client ID. Application type: Web application. Under Authorized redirect URIs add both URIs listed below, exactly as shown. Click Create.",
      "Copy the Client ID (ends in .apps.googleusercontent.com) and the Client secret (starts with GOCSPX-) and paste them below. New clients and redirect URI changes can take a few minutes to become active at Google.",
    ],
  },
};

/** Wording for the OAuth "Set up" modal; falls back to generic labels. */
export function getOAuthSetupGuide(toolkit: string): OAuthSetupGuide {
  const key = (toolkit || "").toLowerCase();
  const known = OAUTH_SETUP_GUIDES[key];
  if (known) return known;
  return {
    displayName: key ? key.charAt(0).toUpperCase() + key.slice(1) : "",
    clientIdLabel: "Client ID",
    clientSecretLabel: "Client Secret",
    clientIdPlaceholder: "Client ID from the developer console",
    clientSecretPlaceholder: "Client secret from the developer console",
    consoleHint: `${key || "the app"} developer console`,
    steps: [
      `Create an OAuth app in the ${key || "provider"} developer console and register both redirect URIs listed below on it.`,
      "Copy the app's client ID and client secret and paste them below.",
    ],
  };
}

/** Google OAuth 2.0 Web client IDs look like 123456789012-abc123.apps.googleusercontent.com. */
const GOOGLE_CLIENT_ID_RE = /\d+-[a-z0-9]+\.apps\.googleusercontent\.com/i;

/** Microsoft Entra Application (client) IDs are GUIDs. */
const MICROSOFT_CLIENT_ID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Toolkits whose OAuth client comes from a Microsoft Entra app registration. */
export function isMicrosoftToolkit(toolkit: string): boolean {
  const key = (toolkit || "").toLowerCase();
  return key.startsWith("microsoft") || key.startsWith("outlook");
}

export interface NormalizedOAuthClientId {
  clientId: string;
  /** Text that preceded the ID and was dropped, typically a browser-autofilled login email. */
  droppedPrefix?: string;
  error?: string;
}

/**
 * Clean a pasted client ID before saving. On stage (2026-10-06) the browser's
 * credential autofill put the admin's login email into the Client ID box and
 * the pasted ID landed after it; Google then answered "401 invalid_client".
 * For Google toolkits, extract the real Web-application client ID and reject
 * anything that is not one (service-account IDs, project numbers, API keys).
 */
export function normalizeOAuthClientId(
  toolkit: string,
  raw: string,
): NormalizedOAuthClientId {
  const value = (raw || "").trim();
  if (isMicrosoftToolkit(toolkit)) {
    const guid = MICROSOFT_CLIENT_ID_RE.exec(value);
    if (!guid) {
      return {
        clientId: value,
        error:
          "That doesn't look like a Microsoft Entra Application (client) ID. Expected a GUID such as 11111111-2222-3333-4444-555555555555 from the app registration's Overview page.",
      };
    }
    const guidPrefix = value.slice(0, guid.index).trim();
    return { clientId: guid[0], droppedPrefix: guidPrefix || undefined };
  }
  if (!toolkit.toLowerCase().startsWith("google")) return { clientId: value };
  const match = GOOGLE_CLIENT_ID_RE.exec(value);
  if (!match) {
    return {
      clientId: value,
      error:
        "That doesn't look like a Google OAuth 2.0 client ID. Expected 123456789012-xxxx.apps.googleusercontent.com from a Web application client (not a service-account ID, project number or API key).",
    };
  }
  const prefix = value.slice(0, match.index).trim();
  return { clientId: match[0], droppedPrefix: prefix || undefined };
}

/** True when the value is itself a Google client ID, i.e. ID and secret were swapped. */
export function looksLikeGoogleClientId(value: string): boolean {
  return GOOGLE_CLIENT_ID_RE.test((value || "").trim());
}

/**
 * Catch the usual secret mix-ups before they reach the provider: a Google
 * client ID pasted as the secret (values swapped), or, for Microsoft toolkits,
 * the client secret's Secret ID (a GUID) pasted instead of its Value.
 * Returns the message to show, or null when the secret looks plausible.
 */
export function validateOAuthClientSecret(
  toolkit: string,
  secret: string,
  secretLabel = "Client Secret",
): string | null {
  const value = (secret || "").trim();
  if (!value) return null;
  if (isMicrosoftToolkit(toolkit)) {
    if (MICROSOFT_CLIENT_ID_RE.test(value) && value.length <= 40) {
      return `The ${secretLabel} field contains a GUID, which is the secret's ID rather than its Value. Paste the Value shown once when the client secret was created.`;
    }
    return null;
  }
  if (looksLikeGoogleClientId(value)) {
    return `The ${secretLabel} field contains a client ID. The two values look swapped.`;
  }
  return null;
}

/**
 * Thrown by connectApp when the API answers OAUTH_APP_SETUP_REQUIRED: the app
 * has no Composio-managed OAuth and no agency OAuth client is saved yet. The
 * page opens the "Set up" modal for `toolkit` instead of showing a bare toast.
 */
export class OAuthSetupRequiredError extends Error {
  readonly toolkit: string;
  readonly redirectUri?: string;

  constructor(message: string, toolkit: string, redirectUri?: string) {
    super(message);
    this.name = "OAuthSetupRequiredError";
    this.toolkit = toolkit;
    this.redirectUri = redirectUri;
    // Required for `instanceof` to work with the ES5 build target.
    Object.setPrototypeOf(this, OAuthSetupRequiredError.prototype);
  }
}

export function isOAuthSetupRequiredError(
  err: unknown,
): err is OAuthSetupRequiredError {
  return (
    err instanceof OAuthSetupRequiredError ||
    (err instanceof Error && err.name === "OAuthSetupRequiredError")
  );
}

export interface OAuthAppRecord {
  toolkit: string;
  scopes: string[];
  configured_by_user_id: string | null;
  is_active: boolean;
  /** "user": the current user's own client; "env": ops default on the API. */
  source?: "user" | "env" | "db";
  user_id?: string | null;
  /** True once the user's first Connect built a Composio auth config from it. */
  has_auth_config?: boolean;
  created_at: string | null;
  updated_at: string | null;
}

// ── Helper Functions ──────────────────────────────────────────────────────────

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem("authToken");
}

/**
 * Build headers for API requests
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  // A 401 from a composio endpoint means the composio auth check failed —
  // this should NOT log the user out of the whole app (which would happen
  // if we called handleUnauthorized here). Show an error on this page instead.
  if (response.status === 401) {
    let detail = "Authentication failed for connected apps";
    try {
      const body = await response.json();
      detail = body?.detail || body?.error?.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || `API error: ${response.status}`);
  }

  return data;
}

// ── API Functions ─────────────────────────────────────────────────────────────

/**
 * Get or create a Composio MCP session for the current user.
 *
 * @param toolkits - Optional list of toolkits to enable (e.g., ['gmail', 'slack'])
 * @returns Session information including available tools
 */
export async function getSession(toolkits?: string[]): Promise<SessionInfo> {
  const response = await fetch(`${COMPOSIO_API_BASE}/session`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ toolkits }),
  });

  const result = await handleResponse<SessionInfo>(response);

  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to get session");
  }

  return result.data;
}

/**
 * Initiate OAuth connection for an app.
 * Opens the OAuth flow in a popup window.
 *
 * @param appName - Name of the app to connect (e.g., 'gmail')
 * @param options - Optional configuration
 * @returns Connection result with auth URL
 */
export async function connectApp(
  appName: string,
  options?: {
    redirectUrl?: string;
    usePopup?: boolean;
  },
): Promise<ConnectAppResult> {
  const response = await fetch(`${COMPOSIO_API_BASE}/connect`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      app_name: appName,
      redirect_url: options?.redirectUrl,
    }),
  });

  // OAUTH_APP_SETUP_REQUIRED (HTTP 400): the toolkit has no Composio-managed
  // OAuth and no agency OAuth client is saved yet. Surface a typed error so
  // the Integrations page can open the "Set up" modal for that toolkit.
  if (!response.ok && response.status !== 401) {
    const body = await response
      .clone()
      .json()
      .catch(() => null);
    if (body?.error?.code === "OAUTH_APP_SETUP_REQUIRED") {
      const details = (body.error.details ?? {}) as Record<string, unknown>;
      throw new OAuthSetupRequiredError(
        body.error.message ||
          "This app needs a one-time OAuth setup by an admin.",
        String(details.toolkit || appName).toLowerCase(),
        typeof details.redirect_uri === "string"
          ? details.redirect_uri
          : undefined,
      );
    }
  }

  const result = await handleResponse<ConnectAppResult>(response);

  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to connect app");
  }

  return result.data;
}

/**
 * Open OAuth flow in a popup window and handle completion.
 *
 * @param authUrl - The OAuth URL to open
 * @param onSuccess - Callback when OAuth completes successfully
 * @param onError - Callback when OAuth fails
 * @returns The popup window reference
 */
export function openOAuthPopup(
  authUrl: string,
  onSuccess?: () => void,
  onError?: (error: string) => void,
): Window | null {
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    authUrl,
    "composio-oauth",
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
  );

  if (!popup) {
    onError?.("Failed to open popup. Please allow popups for this site.");
    return null;
  }

  // Poll for popup closure and check for success/error
  const pollInterval = setInterval(() => {
    if (popup.closed) {
      clearInterval(pollInterval);
      // Check URL params if callback was to same origin
      onSuccess?.();
    }
  }, 500);

  // Set up message listener for cross-origin communication
  const messageHandler = (event: MessageEvent) => {
    if (event.data?.type === "composio-oauth-complete") {
      clearInterval(pollInterval);
      window.removeEventListener("message", messageHandler);

      if (event.data.status === "success") {
        onSuccess?.();
      } else {
        onError?.(event.data.error || "OAuth failed");
      }

      popup.close();
    }
  };

  window.addEventListener("message", messageHandler);

  // Cleanup after timeout (5 minutes)
  setTimeout(
    () => {
      clearInterval(pollInterval);
      window.removeEventListener("message", messageHandler);
    },
    5 * 60 * 1000,
  );

  return popup;
}

/**
 * Get list of connected apps for the current user.
 *
 * @returns List of connected apps with their status
 */
export async function getConnectedApps(): Promise<ConnectedApp[]> {
  const response = await fetch(`${COMPOSIO_API_BASE}/apps`, {
    method: "GET",
    headers: getHeaders(),
  });

  const result = await handleResponse<{ apps: ConnectedApp[]; count: number }>(
    response,
  );

  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to get connected apps");
  }

  return result.data.apps;
}

/**
 * Disconnect an app for the current user.
 *
 * @param appName - Name of the app to disconnect
 */
export async function disconnectApp(appName: string): Promise<void> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/apps/${encodeURIComponent(appName)}`,
    {
      method: "DELETE",
      headers: getHeaders(),
    },
  );

  const result = await handleResponse<{ success: boolean; message: string }>(
    response,
  );

  if (!result.success) {
    throw new Error(result.error?.message || "Failed to disconnect app");
  }
}

/**
 * Execute a Composio tool on behalf of the current user.
 *
 * @param toolName - Name of the tool to execute (e.g., 'GMAIL_SEND_EMAIL')
 * @param params - Parameters for the tool
 * @returns Execution result
 */
export async function executeTool(
  toolName: string,
  params: Record<string, unknown>,
): Promise<ExecuteToolResult> {
  const response = await fetch(`${COMPOSIO_API_BASE}/execute`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      tool_name: toolName,
      params,
    }),
  });

  const result = await handleResponse<ExecuteToolResult>(response);

  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to execute tool");
  }

  return result.data;
}

/**
 * Get list of available apps that can be connected.
 *
 * @returns List of available apps
 */
export async function getAvailableApps(): Promise<AvailableApp[]> {
  try {
    const response = await fetch(`${COMPOSIO_API_BASE}/available-apps`, {
      method: "GET",
      headers: getHeaders(),
    });

    const result = await handleResponse<{
      apps: AvailableApp[];
      count: number;
    }>(response);

    if (result.success && result.data) {
      return result.data.apps.filter(isAppVisible);
    }
  } catch (error) {
    console.warn("Using fallback available apps list:", error);
  }

  // Fallback to local list
  return AVAILABLE_APPS.filter(isAppVisible);
}

/**
 * Check the current user's Composio integration status.
 *
 * @returns User's Composio status
 */
export async function checkComposioStatus(): Promise<ComposioStatus> {
  const response = await fetch(`${COMPOSIO_API_BASE}/status`, {
    method: "GET",
    headers: getHeaders(),
  });

  const result = await handleResponse<ComposioStatus>(response);

  if (!result.success || !result.data) {
    return {
      enabled: true,
      connected_apps_count: 0,
      connected_apps: [],
      has_any_connection: false,
      error: result.error?.message,
    };
  }

  return result.data;
}

/**
 * Save Composio API key to the database.
 *
 * @param apiKey - API key from app.composio.dev/settings/api-keys
 */
export async function saveApiKey(apiKey: string): Promise<void> {
  const response = await fetch(`${COMPOSIO_API_BASE}/config/api-key`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ api_key: apiKey }),
  });

  const result = await handleResponse<{ message: string }>(response);

  if (!result.success) {
    throw new Error(result.error?.message || "Failed to save API key");
  }
}

/**
 * Provision a new Composio project for the authenticated user.
 *
 * The backend uses its server-side organization API key
 * (COMPOSIO_ORG_API_KEY) to call Composio's project-create endpoint,
 * then stores the returned project key encrypted in the database.
 * The project API key itself is never returned to the client.
 *
 * @param projectName - User-typed project name. The backend appends "_IA"
 *                      if the name doesn't already end with it.
 * @returns The created project's id and final (post-suffix) name.
 */
export interface CreateProjectResult {
  project_id: string;
  project_name: string;
  message?: string;
}

/**
 * Provision a new Composio project for the authenticated user.
 *
 * Pass `projectName` to use a custom name (legacy path). Omit it (or pass
 * undefined/empty) and the backend will derive a per-user name from the JWT
 * username — every user in the same agency then gets their own isolated
 * Composio project (so OAuth tokens stay scoped per user, not shared).
 */
export async function createProject(
  projectName?: string,
): Promise<CreateProjectResult> {
  const body: Record<string, unknown> = {};
  if (projectName && projectName.trim()) {
    body.project_name = projectName.trim();
  }
  const response = await fetch(`${COMPOSIO_API_BASE}/project/create`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  const result = await handleResponse<CreateProjectResult>(response);

  if (!result.success || !result.data) {
    throw new Error(
      result.error?.message || "Failed to create Composio project",
    );
  }

  return result.data;
}

// ── Email Trigger Types ───────────────────────────────────────────────────────

export type EmailTriggerProvider = "gmail" | "outlook";

export interface EmailTriggerProviderStatus {
  enabled: boolean;
  trigger: string;
  provider: EmailTriggerProvider;
  updated_at?: string | null;
}

export interface EmailTriggerStatus {
  enabled: boolean;
  trigger: string;
  provider?: EmailTriggerProvider;
  updated_at?: string | null;
  /**
   * Per-provider state, returned by the auto-detect call (no ?provider=) and
   * absent when a specific provider was requested. Gmail and Outlook triggers
   * are independent and can both be on, so a per-mailbox toggle must read this
   * map rather than the top-level `enabled` (the first active provider only).
   */
  providers?: Partial<Record<EmailTriggerProvider, EmailTriggerProviderStatus>>;
}

// ── Email Trigger API Functions ───────────────────────────────────────────────

export async function enableEmailTrigger(
  provider: "gmail" | "outlook" = "gmail",
): Promise<EmailTriggerStatus> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/triggers/email/enable?provider=${provider}`,
    {
      method: "POST",
      headers: getHeaders(),
    },
  );
  const result = await handleResponse<EmailTriggerStatus>(response);
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to enable email trigger");
  }
  return result.data;
}

export async function disableEmailTrigger(
  provider: "gmail" | "outlook" = "gmail",
): Promise<void> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/triggers/email/disable?provider=${provider}`,
    {
      method: "DELETE",
      headers: getHeaders(),
    },
  );
  const result = await handleResponse<{ message: string }>(response);
  if (!result.success) {
    throw new Error(result.error?.message || "Failed to disable email trigger");
  }
}

/**
 * Gets the email trigger status for the current user.
 * Without a provider, returns every provider's state under `providers` (Gmail
 * and Outlook are independent), supporting mixed setups like Outlook login +
 * Gmail connection.
 */
export async function getEmailTriggerStatus(
  provider?: "gmail" | "outlook",
): Promise<EmailTriggerStatus> {
  const url = provider
    ? `${COMPOSIO_API_BASE}/triggers/email/status?provider=${provider}`
    : `${COMPOSIO_API_BASE}/triggers/email/status`;
  const response = await fetch(url, { method: "GET", headers: getHeaders() });
  const result = await handleResponse<EmailTriggerStatus>(response);
  if (!result.success || !result.data) {
    return {
      enabled: false,
      trigger: "GMAIL_NEW_GMAIL_MESSAGE",
      provider: provider ?? "gmail",
    };
  }
  return result.data;
}

// ── Email Label Filter Types & API ────────────────────────────────────────────

export interface GmailLabel {
  id: string;
  name: string;
}

export interface EmailLabelsResponse {
  provider: string;
  labels: GmailLabel[];
  filter_label_name: string | null;
  message?: string;
}


export async function listEmailLabels(
  provider: "gmail" | "outlook" = "gmail",
): Promise<EmailLabelsResponse> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/triggers/email/labels?provider=${provider}`,
    { method: "GET", headers: getHeaders() },
  );
  const result = await handleResponse<EmailLabelsResponse>(response);
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to list email labels");
  }
  return result.data;
}


export async function setEmailFilterLabel(
  labelName: string | null,
  provider: "gmail" | "outlook" = "gmail",
): Promise<{ provider: string; filter_label_name: string | null }> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/triggers/email/label?provider=${provider}`,
    {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({ label_name: labelName }),
    },
  );
  const result = await handleResponse<{ provider: string; filter_label_name: string | null }>(response);
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || "Failed to set email filter label");
  }
  return result.data;
}

// ── Trigger Events Types & API ────────────────────────────────────────────────

export interface TriggerEvent {
  id: number;
  trigger_name: string;
  payload: Record<string, unknown>;
  processed: boolean;
  received_at: string;
}

export async function getTriggerEvents(limit = 10): Promise<TriggerEvent[]> {
  try {
    const response = await fetch(
      `${COMPOSIO_API_BASE}/triggers/events?limit=${limit}`,
      { method: "GET", headers: getHeaders() },
    );
    const result = await handleResponse<{
      events: TriggerEvent[];
      count: number;
    }>(response);
    return result.success && result.data ? result.data.events : [];
  } catch (err) {
    console.warn("[Composio] Failed to fetch trigger events:", err);
    return [];
  }
}

async function getSseTicket(): Promise<string> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/triggers/events/sse-ticket`,
    {
      method: "POST",
      headers: getHeaders(),
    },
  );
  const result = await handleResponse<{ ticket: string }>(response);
  if (!result.success || !result.data?.ticket)
    throw new Error("Failed to get SSE ticket");
  return result.data.ticket;
}

/**
 * Open an SSE stream for real-time trigger events.
 * Uses a short-lived HMAC ticket instead of the JWT in the URL.
 * Returns a cleanup function — call it on component unmount.
 *
 * Re-fetches a fresh ticket on every (re)connect so an expired ticket never
 * silently blocks real-time updates after a network blip.
 */
export function subscribeTriggerEvents(
  onEvent: (event: TriggerEvent) => void,
  onError?: () => void,
): () => void {
  let es: EventSource | null = null;
  let cancelled = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function connect() {
    if (cancelled) return;
    getSseTicket()
      .then((ticket) => {
        if (cancelled) return;
        const url = `${COMPOSIO_API_BASE}/triggers/events/stream?ticket=${encodeURIComponent(ticket)}`;
        es = new EventSource(url);

        es.onmessage = (e) => {
          if (!e.data || e.data.startsWith(":")) return;
          try {
            onEvent(JSON.parse(e.data) as TriggerEvent);
          } catch {
            /* malformed frame — skip */
          }
        };

        es.onerror = () => {
          onError?.();
          // Close the stale connection and reconnect with a fresh ticket after 3 s.
          // The old EventSource auto-reconnect reuses the same (possibly expired) ticket
          // URL, so we manage reconnection ourselves instead.
          es?.close();
          es = null;
          if (!cancelled) {
            retryTimer = setTimeout(connect, 3000);
          }
        };
      })
      .catch(() => {
        onError?.();
        if (!cancelled) {
          retryTimer = setTimeout(connect, 5000);
        }
      });
  }

  connect();

  return () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    es?.close();
  };
}

/**
 * Delete the current user's personal Composio API key.
 * After this call the user must re-add a key before connecting any apps.
 */
export async function deleteApiKey(): Promise<void> {
  const response = await fetch(`${COMPOSIO_API_BASE}/config/api-key`, {
    method: "DELETE",
    headers: getHeaders(),
  });

  const result = await handleResponse<{ message: string }>(response);

  if (!result.success) {
    throw new Error(result.error?.message || "Failed to delete API key");
  }
}

/**
 * Health check for Composio service.
 *
 * @returns True if service is healthy
 */
export async function healthCheck(): Promise<boolean> {
  try {
    const response = await fetch(`${COMPOSIO_API_BASE}/health`, {
      method: "GET",
      headers: getHeaders(),
    });

    const result = await response.json();
    return result.success && result.status === "healthy";
  } catch {
    return false;
  }
}

// ── Composio Client Class ─────────────────────────────────────────────────────

/**
 * ComposioClient class for managing Composio integrations.
 *
 * Usage:
 * ```typescript
 * const client = new ComposioClient();
 * const connectedApps = await client.getConnectedApps();
 * await client.connectApp('gmail');
 * ```
 */
export class ComposioClient {
  /**
   * Get or create a Composio session for the current user.
   */
  async getSession(toolkits?: string[]): Promise<SessionInfo> {
    return getSession(toolkits);
  }

  /**
   * Initiate OAuth connection and return the auth URL.
   */
  async connectApp(appName: string): Promise<string> {
    const result = await connectApp(appName);
    return result.auth_url;
  }

  /**
   * Connect an app using a popup window.
   */
  async connectAppWithPopup(
    appName: string,
    onSuccess?: () => void,
    onError?: (error: string) => void,
  ): Promise<void> {
    const result = await connectApp(appName);
    openOAuthPopup(result.auth_url, onSuccess, onError);
  }

  /**
   * Get list of connected apps for the current user.
   */
  async getConnectedApps(): Promise<ConnectedApp[]> {
    return getConnectedApps();
  }

  /**
   * Disconnect an app.
   */
  async disconnectApp(appName: string): Promise<void> {
    return disconnectApp(appName);
  }

  /**
   * Execute a Composio tool.
   */
  async executeTool(
    toolName: string,
    params: Record<string, unknown>,
  ): Promise<ExecuteToolResult> {
    return executeTool(toolName, params);
  }

  /**
   * Get available apps for connection.
   */
  async getAvailableApps(): Promise<AvailableApp[]> {
    return getAvailableApps();
  }

  /**
   * Check user's Composio status.
   */
  async checkStatus(): Promise<ComposioStatus> {
    return checkComposioStatus();
  }

  /**
   * Save the current user's own OAuth client (Google Chat, DocuSign, ...).
   */
  async saveOAuthAppCredentials(
    toolkit: string,
    clientId: string,
    clientSecret: string,
    scopes?: string,
  ): Promise<void> {
    return saveOAuthAppCredentials(toolkit, clientId, clientSecret, scopes);
  }

  /**
   * List toolkits the current user has saved their own OAuth client for.
   */
  async listOAuthApps(): Promise<OAuthAppRecord[]> {
    return listOAuthApps();
  }

  /**
   * Remove the current user's own OAuth client for a toolkit.
   */
  async deleteOAuthAppCredentials(toolkit: string): Promise<void> {
    return deleteOAuthAppCredentials(toolkit);
  }
}

// ── Per-user OAuth app credentials ────────────────────────────────────────────

/**
 * Save the current user's own OAuth client (client ID + secret) for a toolkit
 * Composio has no managed OAuth app for (Google Chat, DocuSign). Stored
 * encrypted server-side and scoped to this user only, exactly like their own
 * Gmail connection. The user then connects via the standard one-click flow.
 */
export async function saveOAuthAppCredentials(
  toolkit: string,
  clientId: string,
  clientSecret: string,
  scopes?: string,
): Promise<void> {
  const response = await fetch(`${COMPOSIO_API_BASE}/oauth-app-credentials`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      toolkit,
      client_id: clientId,
      client_secret: clientSecret,
      scopes,
    }),
  });
  const result = await handleResponse<{ toolkit: string; message: string }>(
    response,
  );
  if (!result.success) {
    throw new Error(
      result.error?.message || "Failed to save OAuth credentials",
    );
  }
}

/**
 * List toolkits the current user has saved their own OAuth client for (plus any
 * ops env-var defaults). Other users' clients are never included. Never returns secrets.
 */
export async function listOAuthApps(): Promise<OAuthAppRecord[]> {
  const response = await fetch(`${COMPOSIO_API_BASE}/oauth-app-credentials`, {
    method: "GET",
    headers: getHeaders(),
  });
  const result = await handleResponse<{ oauth_apps: OAuthAppRecord[] }>(
    response,
  );
  if (!result.success || !result.data) {
    return [];
  }
  return result.data.oauth_apps || [];
}

/**
 * Remove the current user's own OAuth client for a toolkit (and the Composio
 * auth config and connection built from it).
 */
export async function deleteOAuthAppCredentials(
  toolkit: string,
): Promise<void> {
  const response = await fetch(
    `${COMPOSIO_API_BASE}/oauth-app-credentials/${encodeURIComponent(toolkit)}`,
    {
      method: "DELETE",
      headers: getHeaders(),
    },
  );
  const result = await handleResponse<{ toolkit: string; message: string }>(
    response,
  );
  if (!result.success) {
    throw new Error(
      result.error?.message || "Failed to delete OAuth credentials",
    );
  }
}

// ── Default Export ────────────────────────────────────────────────────────────

export default new ComposioClient();
